import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, ContentItem, Client } from '../lib/api';
import {
  CheckCircleIcon,
  PencilSquareIcon,
  ChevronDownIcon,
  BuildingOffice2Icon,
  SparklesIcon,
  HomeIcon,
  PlusIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import ImageSelector from '../components/ImageSelector';
import WebsitePreview from '../components/WebsitePreview';
import SocialPreview from '../components/SocialPreview';
import SMSPreview from '../components/SMSPreview';

const CONTENT_META: Record<string, { icon: string; description: string }> = {
  scout_report: { icon: '\uD83D\uDD0D', description: 'Business research & market analysis' },
  seo_report: { icon: '\uD83D\uDCCA', description: 'Local SEO keywords & opportunities' },
  google_reviews: { icon: '\u2B50', description: 'What your clients are saying online' },
  compliance_profile: { icon: '\uD83D\uDEE1\uFE0F', description: 'Regulations & marketing guidelines' },
  brand_guide: { icon: '\uD83C\uDFA8', description: 'Colors, voice & messaging' },
  primary_profile: { icon: '\uD83C\uDFAF', description: 'Your ideal client profile' },
  specialty_profiles: { icon: '\uD83D\uDC65', description: 'Specialty audience segments' },
  captured_images: { icon: '\uD83D\uDCF8', description: 'Images from your website' },
  sms_campaigns: { icon: '\uD83D\uDCF1', description: 'Automated text message campaigns' },
  sms_templates: { icon: '\uD83D\uDCAC', description: 'Re-engagement text messages' },
  welcome_sequence: { icon: '\uD83D\uDCE7', description: 'New client welcome email series' },
};

const PHASE_NAMES: Record<string, string> = {
  'business-profile': 'Business Profile',
  'business-intel': 'Business Intelligence',
  'brand-guide': 'Brand Guide',
  'icp': 'Ideal Client Profiles',
  'assets': 'Assets & Images',
  'website': 'Website & Funnel',
  'blog': 'Blog Content',
  'social': 'Social Media',
  'messaging': 'Follow-Up Messaging',
  'launch': 'Launch & Activation',
};

export default function ContentReviewPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [contentItems, setContentItems] = useState<ContentItem[]>([]);
  const [loadedItems, setLoadedItems] = useState<Record<string, ContentItem>>({});
  const [activeItemId, setActiveItemId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isApproving, setIsApproving] = useState(false);
  const [isRevising, setIsRevising] = useState(false);
  const [revisionSubmitted, setRevisionSubmitted] = useState(false);
  const [showRevisionForm, setShowRevisionForm] = useState(false);
  const [revisionFeedback, setRevisionFeedback] = useState('');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  // Phase 4 (ICP): add specialty profiles
  const [specialties, setSpecialties] = useState<string[]>(['']);
  const [isAddingSpecialties, setIsAddingSpecialties] = useState(false);
  const [addSpecialtiesSuccess, setAddSpecialtiesSuccess] = useState(false);
  const [addSpecialtiesError, setAddSpecialtiesError] = useState<string | null>(null);
  const [strategyExplainer, setStrategyExplainer] = useState<{
    html: string; strategy_name: string; strategy_tagline: string;
  } | null>(null);
  const [businessProfile, setBusinessProfile] = useState<Client | null>(null);

  const handleAddSpecialty = () => setSpecialties(prev => [...prev, '']);
  const handleUpdateSpecialty = (i: number, val: string) =>
    setSpecialties(prev => prev.map((s, idx) => idx === i ? val : s));
  const handleRemoveSpecialty = (i: number) =>
    setSpecialties(prev => prev.filter((_, idx) => idx !== i));
  const handleSubmitSpecialties = async () => {
    if (!slug) return;
    const valid = specialties.map(s => s.trim()).filter(Boolean);
    if (!valid.length) return;
    setIsAddingSpecialties(true);
    setAddSpecialtiesError(null);
    try {
      await api.addSpecialties(slug, valid);
      setAddSpecialtiesSuccess(true);
      setSpecialties(['']);
    } catch (err: any) {
      setAddSpecialtiesError(err?.response?.data?.error || 'Failed to generate specialty profiles');
    } finally {
      setIsAddingSpecialties(false);
    }
  };

  useEffect(() => {
    loadContent();
  }, [slug]);

  const loadContent = async () => {
    if (!slug) return;
    try {
      // Always load business profile for name in previews
      try {
        const profile = await api.getCurrentUser();
        setBusinessProfile(profile);
      } catch {}
      if (slug === 'business-profile') {
        // Already loaded above
      } else {
        const response = await api.getPhaseReview(slug);
        setContentItems(response.content_items);
        if (response.strategy_explainer) {
          setStrategyExplainer(response.strategy_explainer);
        }
        if (response.content_items.length > 0) {
          const first = response.content_items[0];
          setActiveItemId(first.id);
          try {
            const full = await api.getContentItem(first.id);
            setLoadedItems({ [first.id]: full });
          } catch {}
        }
      }
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const selectItem = async (item: ContentItem) => {
    setActiveItemId(item.id);
    setMobileNavOpen(false);
    if (!loadedItems[item.id]) {
      try {
        const full = await api.getContentItem(item.id);
        setLoadedItems((prev) => ({ ...prev, [item.id]: full }));
      } catch (error) {
        console.error('Failed to load content:', error);
      }
    }
  };

  const handleApprove = async () => {
    if (!slug) return;
    setIsApproving(true);
    try {
      await api.approvePhase(slug);
      if (slug === 'messaging') {
        navigate('/activation');
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to approve:', error);
    } finally {
      setIsApproving(false);
    }
  };

  const handleRequestRevision = async () => {
    if (!slug || !revisionFeedback.trim()) return;
    setIsRevising(true);
    try {
      await api.requestRevision(slug, revisionFeedback);
      setShowRevisionForm(false);
      setRevisionFeedback('');
      setRevisionSubmitted(true);
      setTimeout(() => navigate('/'), 3500);
    } catch (error) {
      console.error('Failed to request revision:', error);
    } finally {
      setIsRevising(false);
    }
  };


  if (revisionSubmitted) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-6">
        <div className="bg-sage-50 border border-sage-200 rounded-2xl p-10 max-w-md w-full shadow-sm">
          <div className="text-4xl mb-4">🌲</div>
          <h2 className="text-xl font-bold text-charcoal-500 mb-2">The Rangers are on it.</h2>
          <p className="text-charcoal-300 text-sm leading-relaxed">Your revision request has been submitted. The Rangers are already working on your updates — head back to your dashboard to track progress.</p>
          <p className="text-xs text-charcoal-200 mt-4">Returning to dashboard…</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="relative">
          <div className="animate-spin rounded-full h-14 w-14 border-4 border-copper-200 border-t-copper-500"></div>
          <SparklesIcon className="h-6 w-6 text-copper-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />
        </div>
        <p className="text-charcoal-400 mt-4 text-sm">Loading your materials...</p>
      </div>
    );
  }

  const phaseName = PHASE_NAMES[slug || ''] || 'Phase';
  const activeItem = activeItemId ? loadedItems[activeItemId] : null;
  const activeListItem = contentItems.find((i) => i.id === activeItemId);

  /* Business Profile special case */
  if (slug === 'business-profile' && businessProfile) {
    return (
      <div className="max-w-4xl mx-auto pb-8">
        <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cream-100 text-charcoal-500 hover:bg-copper-100 hover:text-copper-700 transition-colors text-sm font-medium border border-charcoal-100 mb-6">
          <HomeIcon className="h-4 w-4 mr-1.5" />
          Back to Your Journey
        </Link>
        <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm p-6 md:p-8">
          <div className="h-1 absolute top-0 left-0 right-0 bg-gradient-to-r from-copper-400 via-sage-400 to-teal-400" />
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2.5 bg-copper-100 rounded-xl">
              <BuildingOffice2Icon className="h-6 w-6 text-copper-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-charcoal-500">Business Profile</h1>
              <p className="text-charcoal-300 text-sm">Your submitted business information</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { label: 'Business Name', value: businessProfile.business_name },
              { label: 'Business Type', value: businessProfile.business_type?.replace('_', ' ') },
              { label: 'Contact Email', value: businessProfile.email },
              { label: 'Location', value: businessProfile.location },
              { label: 'Country', value: businessProfile.country },
              { label: 'Status', value: businessProfile.status },
            ].map((field) => (
              <div key={field.label} className="p-3 rounded-xl bg-cream-50">
                <p className="text-xs font-medium text-charcoal-300 uppercase tracking-wider">{field.label}</p>
                <p className="text-charcoal-500 font-medium mt-1 capitalize">{field.value || '\u2014'}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  /* Empty state */
  if (contentItems.length === 0) {
    return (
      <div className="max-w-4xl mx-auto pb-8">
        <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cream-100 text-charcoal-500 hover:bg-copper-100 hover:text-copper-700 transition-colors text-sm font-medium border border-charcoal-100 mb-6">
          <HomeIcon className="h-4 w-4 mr-1.5" />
          Back to Your Journey
        </Link>
        <div className="relative overflow-hidden bg-white rounded-2xl border border-charcoal-100 shadow-sm text-center py-16 px-6">
          <SparklesIcon className="h-10 w-10 text-copper-300 mx-auto mb-3" />
          <p className="text-charcoal-500 font-medium">Content is being generated...</p>
          <p className="text-charcoal-300 text-sm mt-1">This usually takes a minute or two.</p>
          <button onClick={loadContent} className="text-copper-600 text-sm mt-4 hover:underline font-medium">
            Refresh
          </button>
        </div>
      </div>
    );
  }

  /* Render active content */
  const renderContent = () => {
    // ICP specialty profiles — always show form so new ones can be added
    if (slug === 'icp' && activeListItem?.content_key === 'specialty_profiles') {
      const hasContent = activeItem && (activeItem.content_html || activeItem.content);
      return (
        <div className="space-y-8">
          {/* Existing specialty content (if any) */}
          {hasContent && activeItem && (
            <div>
              {activeItem.content_html ? (
                <div
                  className="prose prose-sm md:prose-base max-w-none text-charcoal-500 [&_h1]:text-charcoal-500 [&_h2]:text-charcoal-500 [&_h3]:text-charcoal-500 [&_strong]:text-charcoal-500"
                  dangerouslySetInnerHTML={{ __html: activeItem.content_html }}
                />
              ) : (
                <pre className="whitespace-pre-wrap text-sm text-charcoal-500 leading-relaxed">{activeItem.content}</pre>
              )}
            </div>
          )}

          {/* Add Specialty Profiles form */}
          <div className="border border-charcoal-100 rounded-xl p-5 bg-cream-50/50 space-y-4">
            <div>
              <h3 className="text-base font-semibold text-charcoal-500">
                {hasContent ? 'Add More Specialty Profiles' : 'Generate Specialty Profiles'}
              </h3>
              <p className="text-sm text-charcoal-300 mt-1">
                Add specialty services or patient types to generate targeted ideal client profiles for each one.
              </p>
            </div>

            {addSpecialtiesSuccess ? (
              <div className="flex items-start gap-3 bg-sage-50 border border-sage-200 rounded-lg px-4 py-3">
                <CheckCircleIcon className="h-5 w-5 text-sage-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-sage-800">
                  <p className="font-medium">Specialty profiles are being generated.</p>
                  <p className="text-sage-600 mt-0.5">Check back in a minute — they'll appear here when ready.</p>
                  <button
                    onClick={() => { setAddSpecialtiesSuccess(false); loadContent(); }}
                    className="mt-2 text-sage-700 underline text-xs"
                  >
                    Refresh now
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {specialties.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={s}
                      onChange={e => handleUpdateSpecialty(i, e.target.value)}
                      placeholder={`e.g. Pediatric Patients, Sports Rehab`}
                      className="flex-1 border border-charcoal-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-copper-300 focus:border-copper-400"
                    />
                    {specialties.length > 1 && (
                      <button onClick={() => handleRemoveSpecialty(i)} className="text-charcoal-300 hover:text-red-500 transition-colors">
                        <XMarkIcon className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                ))}

                <button
                  onClick={handleAddSpecialty}
                  className="inline-flex items-center gap-1.5 text-sm text-copper-600 hover:text-copper-700 font-medium"
                >
                  <PlusIcon className="h-4 w-4" />
                  Add Another Specialty
                </button>

                {addSpecialtiesError && (
                  <p className="text-xs text-red-500">{addSpecialtiesError}</p>
                )}

                <button
                  onClick={handleSubmitSpecialties}
                  disabled={isAddingSpecialties || !specialties.some(s => s.trim())}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2.5 btn btn-primary text-sm font-semibold rounded-xl disabled:opacity-50"
                >
                  <SparklesIcon className="h-4 w-4" />
                  {isAddingSpecialties ? 'Generating...' : 'Generate Specialty Profiles'}
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    if (!activeItem) {
      return (
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-6 w-6 border-2 border-copper-200 border-t-copper-500"></div>
          <span className="ml-3 text-sm text-charcoal-300">Loading...</span>
        </div>
      );
    }

    if (activeItem.content_key === 'captured_images') {
      return <ImageSelector contentId={activeItem.id} contentJson={activeItem.content} />;
    }
    if (slug === 'website' && activeItem.content_html) {
      return <WebsitePreview html={activeItem.content_html} />;
    }
    if (slug === 'social' && activeItem.content) {
      return (
        <SocialPreview
          businessName={businessProfile?.business_name || 'Your Business'}
          postText={activeItem.content}
          hashtags={activeItem.metadata?.hashtags}
        />
      );
    }
    if (activeItem.content_type === 'sms' || activeItem.content_key === 'sms_campaigns' || activeItem.content_key === 'sms_templates') {
      return (
        <div className="space-y-6">
          {activeItem.content_html && (
            <div
              className="prose prose-sm max-w-none text-charcoal-500 [&_h3]:text-charcoal-500 [&_p]:text-charcoal-400"
              dangerouslySetInnerHTML={{ __html: activeItem.content_html }}
            />
          )}
          {activeItem.content && (
            <SMSPreview
              businessName={businessProfile?.business_name || 'Your Business'}
              messages={[{ text: activeItem.content, type: 'outbound' }]}
            />
          )}
        </div>
      );
    }
    if (activeItem.content_type === 'email' && activeItem.content_html) {
      return (
        <div className="w-full">
          <iframe
            srcDoc={activeItem.content_html}
            className="w-full border-0 rounded-xl"
            style={{ minHeight: '400px' }}
            title="Email Preview"
            onLoad={(e) => {
              const iframe = e.currentTarget;
              if (iframe.contentDocument?.body) {
                iframe.style.height = (iframe.contentDocument.body.scrollHeight + 40) + 'px';
              }
            }}
          />
        </div>
      );
    }
    if (activeItem.content_html) {
      return (
        <div
          className="prose prose-sm md:prose-base max-w-none text-charcoal-500 [&_h1]:text-charcoal-500 [&_h2]:text-charcoal-500 [&_h3]:text-charcoal-500 [&_strong]:text-charcoal-500"
          dangerouslySetInnerHTML={{ __html: activeItem.content_html }}
        />
      );
    }
    if (activeItem.content) {
      return <pre className="whitespace-pre-wrap text-sm text-charcoal-500 leading-relaxed">{activeItem.content}</pre>;
    }
    return <p className="text-charcoal-300 italic">No content available for this item.</p>;
  };

  const getItemMeta = (item: ContentItem) => {
    if (CONTENT_META[item.content_key]) return CONTENT_META[item.content_key];
    // Type-based fallback for individual items
    if (item.content_type === 'sms') return { icon: '\uD83D\uDCF1', description: 'Text message template' };
    if (item.content_type === 'email') return { icon: '\uD83D\uDCE7', description: 'Email template' };
    if (item.content_type === 'email_sequence') return { icon: '\uD83D\uDCE7', description: 'Email sequence' };
    if (item.content_type === 'sms_template') return { icon: '\uD83D\uDCF1', description: 'SMS campaign' };
    return { icon: '\uD83D\uDCC4', description: item.content_key?.replace(/_/g, ' ') || item.content_type.replace(/_/g, ' ') };
  };

  /* Main Layout: Sidebar + Content */
  return (
    <div className="pb-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <Link to="/" className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-cream-100 text-charcoal-500 hover:bg-copper-100 hover:text-copper-700 transition-colors text-sm font-medium border border-charcoal-100 mb-2">
            <HomeIcon className="h-4 w-4 mr-1.5" />
            Back to Your Journey
          </Link>
          <h1 className="text-2xl font-bold text-charcoal-500">{phaseName}</h1>
          <p className="text-charcoal-300 text-sm mt-0.5">{contentItems.length} item{contentItems.length !== 1 ? 's' : ''} ready for your review</p>
        </div>
      </div>

      {/* Strategy explainer */}
      {strategyExplainer && (
        <div className="bg-gradient-to-r from-copper-50 to-copper-100 border border-copper-200 rounded-xl px-5 py-3 mb-6 flex items-start gap-3">
          <SparklesIcon className="h-5 w-5 text-copper-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-copper-800">
            <span className="font-semibold">{strategyExplainer.strategy_name}</span>
            {strategyExplainer.strategy_tagline && (
              <span className="text-copper-600"> &mdash; {strategyExplainer.strategy_tagline}</span>
            )}
          </div>
        </div>
      )}

      {/* Mobile Nav Dropdown */}
      <div className="md:hidden mb-4">
        <button
          onClick={() => setMobileNavOpen(!mobileNavOpen)}
          className="w-full flex items-center justify-between bg-white border border-charcoal-100 rounded-xl px-4 py-3 text-left shadow-sm"
        >
          <div className="flex items-center gap-2">
            <span>{activeListItem ? getItemMeta(activeListItem).icon : ''}</span>
            <span className="text-sm font-medium text-charcoal-500 truncate">
              {activeListItem?.title || 'Select an item'}
            </span>
          </div>
          <ChevronDownIcon className={`h-4 w-4 text-charcoal-300 transition-transform ${mobileNavOpen ? 'rotate-180' : ''}`} />
        </button>
        {mobileNavOpen && (
          <div className="mt-1 bg-white border border-charcoal-100 rounded-xl shadow-lg overflow-hidden z-20 relative">
            {contentItems.map((item) => {
              const meta = getItemMeta(item);
              const isActive = item.id === activeItemId;
              return (
                <button
                  key={item.id}
                  onClick={() => selectItem(item)}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors ${
                    isActive ? 'bg-copper-50 border-l-2 border-copper-500' : 'hover:bg-cream-50'
                  }`}
                >
                  <span className="text-lg flex-shrink-0">{meta.icon}</span>
                  <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${isActive ? 'text-copper-700' : 'text-charcoal-500'}`}>
                      {item.title}
                    </p>
                    <p className="text-xs text-charcoal-300 truncate">{meta.description}</p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Desktop: Sidebar + Main */}
      <div className="flex gap-6">
        {/* Left Sidebar */}
        <aside className="hidden md:flex flex-col w-64 flex-shrink-0">
          <div className="relative overflow-hidden bg-white border border-charcoal-100 rounded-2xl shadow-sm sticky top-6">
            <div className="h-1 bg-gradient-to-r from-copper-400 via-sage-400 to-teal-400" />
            <div className="px-4 py-3 border-b border-charcoal-100 bg-cream-50/50">
              <p className="text-xs font-semibold text-charcoal-400 uppercase tracking-wider">Materials</p>
            </div>
            <nav className="max-h-[calc(100vh-20rem)] overflow-y-auto">
              {contentItems.map((item) => {
                const meta = getItemMeta(item);
                const isActive = item.id === activeItemId;
                return (
                  <button
                    key={item.id}
                    onClick={() => selectItem(item)}
                    className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-charcoal-50 transition-colors ${
                      isActive
                        ? 'bg-copper-50 border-l-2 border-l-copper-500'
                        : 'hover:bg-cream-50 border-l-2 border-l-transparent'
                    }`}
                  >
                    <span className="text-lg flex-shrink-0">{meta.icon}</span>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${isActive ? 'text-copper-700' : 'text-charcoal-500'}`}>
                        {item.title}
                      </p>
                      <p className="text-xs text-charcoal-300 truncate">{meta.description}</p>
                    </div>
                  </button>
                );
              })}
            </nav>

            {/* Actions in sidebar */}
            <div className="p-4 border-t border-charcoal-100 space-y-2">
              {!showRevisionForm ? (
                <>
                  <button
                    onClick={handleApprove}
                    disabled={isApproving}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 btn btn-primary text-sm font-semibold rounded-xl shadow-sm shadow-copper-500/20"
                  >
                    <CheckCircleIcon className="h-4 w-4" />
                    {isApproving ? 'Approving...' : 'Approve & Continue'}
                  </button>
                  <button
                    onClick={() => setShowRevisionForm(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 btn btn-outline text-sm font-medium rounded-xl"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    Request Changes
                  </button>
                </>
              ) : (
                <>
                  <textarea
                    value={revisionFeedback}
                    onChange={(e) => setRevisionFeedback(e.target.value)}
                    placeholder="What would you like changed?"
                    className="w-full border border-charcoal-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-copper-300 focus:border-copper-400 resize-none"
                    rows={3}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleRequestRevision}
                      disabled={!revisionFeedback.trim() || isRevising}
                      className="flex-1 px-3 py-2 btn btn-primary text-sm font-semibold rounded-lg disabled:opacity-50"
                    >
                      {isRevising ? 'Updating...' : 'Submit'}
                    </button>
                    <button
                      onClick={() => { setShowRevisionForm(false); setRevisionFeedback(''); }}
                      className="px-3 py-2 btn btn-outline text-sm rounded-lg"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </aside>

        {/* Main Content Pane */}
        <main className="flex-1 min-w-0">
          <div className="relative overflow-hidden bg-white border border-charcoal-100 rounded-2xl shadow-sm">
            <div className="h-1 bg-gradient-to-r from-copper-400 via-sage-400 to-teal-400" />
            {/* Content header bar */}
            {activeListItem && (
              <div className="px-6 py-4 border-b border-charcoal-100 bg-cream-50/50 flex items-center gap-3">
                <span className="text-xl">{getItemMeta(activeListItem).icon}</span>
                <div>
                  <h2 className="text-lg font-semibold text-charcoal-500">{activeListItem.title}</h2>
                  <p className="text-xs text-charcoal-300">{getItemMeta(activeListItem).description}</p>
                </div>
              </div>
            )}
            {/* Content body */}
            <div className="p-6 md:p-8">
              {renderContent()}
            </div>
          </div>

          {/* Mobile action buttons */}
          <div className="md:hidden mt-4 space-y-2">
            {!showRevisionForm ? (
              <>
                <button
                  onClick={handleApprove}
                  disabled={isApproving}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 btn btn-primary text-sm font-semibold rounded-xl shadow-sm shadow-copper-500/20"
                >
                  <CheckCircleIcon className="h-5 w-5" />
                  {isApproving ? 'Approving...' : 'Approve & Continue'}
                </button>
                <button
                  onClick={() => setShowRevisionForm(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 btn btn-outline text-sm font-medium rounded-xl"
                >
                  <PencilSquareIcon className="h-5 w-5" />
                  Request Changes
                </button>
              </>
            ) : (
              <div className="bg-white border border-charcoal-100 rounded-xl p-4 space-y-3">
                <textarea
                  value={revisionFeedback}
                  onChange={(e) => setRevisionFeedback(e.target.value)}
                  placeholder="What would you like changed?"
                  className="w-full border border-charcoal-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-copper-300 focus:border-copper-400 resize-none"
                  rows={3}
                />
                <div className="flex gap-2">
                  <button
                    onClick={handleRequestRevision}
                    disabled={!revisionFeedback.trim() || isRevising}
                    className="flex-1 px-3 py-3 btn btn-primary text-sm font-semibold rounded-lg disabled:opacity-50"
                  >
                    {isRevising ? 'Updating...' : 'Submit'}
                  </button>
                  <button
                    onClick={() => { setShowRevisionForm(false); setRevisionFeedback(''); }}
                    className="px-4 py-3 btn btn-outline text-sm rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
